import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';
import CardStack from '../components/Discover/CardStack';
import FilterPanel from '../components/Discover/FilterPanel';

const styles = {
  container: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
  },
  header: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: '8px 16px',
    flexShrink: 0,
  },
  filterBtn: {
    background: 'var(--bg-elevated)',
    border: '1px solid var(--border-color)',
    color: 'var(--text-secondary)',
    padding: '6px 14px',
    borderRadius: 'var(--radius-full)',
    fontSize: '13px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  stackArea: {
    flex: 1,
    position: 'relative',
    overflow: 'hidden',
  },
  onboardingBanner: {
    background: 'linear-gradient(135deg, rgba(232,87,109,0.15), rgba(232,87,109,0.05))',
    border: '1px solid rgba(232,87,109,0.3)',
    borderRadius: 'var(--radius-md)',
    margin: '0 16px 8px',
    padding: '12px 16px',
  },
  bannerContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
  },
  bannerText: {
    fontSize: '13px',
    color: 'var(--text-primary)',
    flex: 1,
  },
  bannerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexShrink: 0,
  },
  bannerBtn: {
    padding: '6px 16px',
    fontSize: '12px',
    borderRadius: 'var(--radius-full)',
  },
  bannerDismiss: {
    background: 'none',
    border: 'none',
    color: 'var(--text-muted)',
    fontSize: '18px',
    cursor: 'pointer',
    padding: '0 4px',
    lineHeight: 1,
  },
};

export default function DiscoverPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({});
  const [filtersLoaded, setFiltersLoaded] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(
    () => localStorage.getItem('onboarding_banner_dismissed') === '1'
  );

  const showOnboardingBanner = user && !user.onboarding_complete && !bannerDismissed;

  const dismissBanner = () => {
    setBannerDismissed(true);
    localStorage.setItem('onboarding_banner_dismissed', '1');
  };

  // Load saved filters from server on mount
  useEffect(() => {
    const loadFilters = async () => {
      try {
        const res = await api.get('/users/filters');
        setFilters(res.data);
      } catch {
        // use defaults
      } finally {
        setFiltersLoaded(true);
      }
    };
    loadFilters();
  }, []);

  const hasActiveFilters =
    filters.gender_pref?.length > 0 ||
    filters.vaccine_pref?.length > 0 ||
    (filters.min_age && filters.min_age !== 18) ||
    (filters.max_age && filters.max_age !== 50) ||
    (filters.max_distance && filters.max_distance !== 50);

  return (
    <div style={styles.container}>
      {showOnboardingBanner && (
        <div style={styles.onboardingBanner}>
          <div style={styles.bannerContent}>
            <span style={styles.bannerText}>
              Finish setting up your profile to improve your matches
            </span>
            <div style={styles.bannerActions}>
              <button
                className="btn btn-primary"
                style={styles.bannerBtn}
                onClick={() => navigate('/onboarding/voice')}
              >
                Continue
              </button>
              <button
                style={styles.bannerDismiss}
                onClick={dismissBanner}
                aria-label="Dismiss"
              >
                &times;
              </button>
            </div>
          </div>
        </div>
      )}
      <div style={styles.header}>
        <button
          style={{
            ...styles.filterBtn,
            ...(hasActiveFilters
              ? { borderColor: 'var(--accent)', color: 'var(--accent)' }
              : {}),
          }}
          onClick={() => setShowFilters(true)}
        >
          Filters {hasActiveFilters ? '●' : ''}
        </button>
      </div>

      <div style={styles.stackArea}>
        {filtersLoaded && <CardStack filters={filters} onOpenFilters={() => setShowFilters(true)} />}
      </div>

      {showFilters && (
        <FilterPanel
          filters={filters}
          onApply={setFilters}
          onClose={() => setShowFilters(false)}
        />
      )}
    </div>
  );
}
